/**
 * The upload story, whole: sniff-gate (unsupported files never leave
 * the machine), web-ifc instant preview at t=0 (gray = local,
 * provisional), the INTAKE DOOR as the one ingest trigger (PRD-80,
 * 2026-10-02, after the session door of 2026-09-10: the api mints a
 * PUT url for one object in the org's data bucket, the bytes go
 * straight to storage with a needle, the api is told which object
 * arrived and announces the document -- the multipart route 413'd on
 * Cloud Run at 32 MB, and no body crosses it now), and live parse progress off the
 * stream. Completion needs no polling -- the 'parsed' event both ends
 * the progress AND invalidates the viewer's elements query through the
 * api bridge.
 *
 * The preview RENDERS elsewhere: this panel only elects the file and
 * hands it up through onPreview -- the shell owns the main canvas
 * slot and places IfcPreview there (modules never reach the frame).
 * 'parsed' hands up null: server truth replaces the provisional gray.
 */
import { useEffect, useRef, useState } from "react";
import { ActionButton, Dropzone, Notice, RouteLink, Stack, Text } from "../../../strata";
import {
  subscribeStream,
  type components,
  typedClient,
} from "../../../api";
import { putToUrl, type PutHandle } from "../session";
import { type IfcSniff, isSupportedSchema, sniffIfc } from "../sniff";

type IntakeUrl = components["schemas"]["IntakeUrl"];
type ValidationError = components["schemas"]["ValidationError"];
type HTTPValidationError = components["schemas"]["HTTPValidationError"];

/** Type guard: check if an error response is a Pydantic validation error (422). */
function isValidationError(body: unknown): body is HTTPValidationError {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return false;
  const detail: unknown = (body as { detail: unknown }).detail;
  return (
    Array.isArray(detail) &&
    detail.every(
      (item: unknown) =>
        typeof item === 'object' &&
        item !== null &&
        'msg' in item &&
        typeof (item as { msg: unknown }).msg === 'string',
    )
  );
}

/** Type guard: check if an error response has a string detail field. */
function isStringDetail(body: unknown): body is { detail: string } {
  return (
    typeof body === 'object' &&
    body !== null &&
    'detail' in body &&
    typeof (body as Record<string, unknown>).detail === 'string'
  );
}

/** Convert a client response to an error message. */
function errorMessage(response: { data?: unknown; response?: Response }): string {
  const { data, response: res } = response;
  if (res?.status === 403) {
    return "This account has no organization yet -- onboard first.";
  }
  if (typeof data === 'string') {
    return data;
  }
  if (isStringDetail(data)) {
    return data.detail;
  }
  if (isValidationError(data)) {
    const messages = (data.detail as ValidationError[])
      .map((error) => error.msg)
      .join('; ');
    if (messages) {
      return messages;
    }
  }
  return `upload refused: ${res?.status}`;
}

// Keyed by staging file: parse events are per-FILE (payload.file), and
// collapsing them into one counter mixes concurrent ingests into
// nonsense (seen live on staging: "512 / 256" -- one run's staged
// count over another run's total).
type IngestProgress = Map<string, { staged: number; total: number }>;

export function UploadPanel({
  projectId,
  onPreview,
}: {
  projectId: string;
  onPreview: (file: File | null) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sniff, setSniff] = useState<IfcSniff | null>(null);
  const [progress, setProgressState] = useState<IngestProgress>(new Map());
  // The map in force, readable synchronously: a stream event decides "the last file cleared"
  // from it, which a React updater (run lazily, at render) cannot answer in time.
  const progressRef = useRef<IngestProgress>(new Map());
  const setProgress = (next: IngestProgress) => {
    progressRef.current = next;
    setProgressState(next);
  };
  // The put's needle: bytes sent of the file's size, null once the bytes are storage's.
  const [sent, setSent] = useState<{ bytes: number; total: number } | null>(
    null,
  );
  // AN ARRIVAL THE DOOR REFUSED (2026-09-11): the bytes ARE in the bucket and the api keeps both
  // objects for exactly this retry, so the object is held here rather than asking a person to
  // send a hundred megabytes again.
  const [unannounced, setUnannounced] = useState<{
    objectName: string;
    name: string;
  } | null>(null);
  // The running put, so leaving the screen stops it instead of letting it finish unwatched.
  const running = useRef<PutHandle | null>(null);

  // A PROJECT IS A DIFFERENT PANEL: its predecessor's counters describe a file this project never
  // had, and the filter below would never clear them (2026-09-11).
  useEffect(() => {
    setProgress(new Map());
    setUploading(false);
    setUnannounced(null);
    setSent(null);
  }, [projectId]);

  // A put still running when this panel goes is stopped, not left to finish unwatched.
  useEffect(() => {
    return () => running.current?.abort();
  }, []);

  useEffect(() => {
    return subscribeStream((event) => {
      if (event.event_type !== "document_progress" || event.payload.project_id !== projectId) return;
      const file = String(event.payload.file ?? "");
      const stage = event.payload.stage;
      const prev = progressRef.current;
      if (stage === "parsing") {
        setProgress(
          new Map(prev).set(file, {
            staged: 0,
            total: Number(event.payload.elements ?? 0),
          }),
        );
      } else if (stage === "streaming") {
        const staged = Number(event.payload.staged ?? 0);
        setProgress(
          new Map(prev).set(file, {
            staged,
            total: prev.get(file)?.total ?? staged,
          }),
        );
      } else if (stage === "parsed") {
        // PER FILE (2026-09-20): the producer exists and names the file, so only this file's
        // counter clears; the panel is done when the last one has. The side effects fire
        // here, once, from the map in force -- never inside a state updater.
        const next = new Map(prev);
        next.delete(file);
        setProgress(next);
        if (next.size === 0) {
          setUploading(false);
          onPreview(null);
        }
      }
    });
  }, [projectId, onPreview]);

  /**
   * Name the object the bytes landed in, and hold it when the door cannot say so.
   *
   * The api answers 503 when it cannot announce, keeps both the addressed object and the
   * pending object, and asks for the arrival again. Holding the object's name here is what makes
   * that retry a button rather than a re-upload (2026-09-11).
   */
  const announceArrival = async (objectName: string, name: string) => {
    const response = await typedClient.POST('/v1/intake/arrived', {
      body: {
        project_id: projectId,
        object_name: objectName,
        document_name: name,
        marking: "unmarked",
      },
    });
    if (response.error) {
      setUploadError(response.error instanceof Error ? response.error.message : String(response.error));
      setUnannounced({ objectName, name });
      setUploading(false);
      return;
    }
    if (!response.response?.ok) {
      setUploadError(errorMessage(response));
      setUnannounced({ objectName, name });
      setUploading(false);
      return;
    }
    setUnannounced(null);
  };

  const retryArrival = async () => {
    if (!unannounced) return;
    setUploadError(null);
    setUploading(true);
    try {
      await announceArrival(unannounced.objectName, unannounced.name);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
      setUploading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    const fileSniff = await sniffIfc(file);
    setSniff(fileSniff);
    if (!isSupportedSchema(fileSniff.schema)) {
      setUploadError(`Unsupported IFC schema: ${fileSniff.schema ?? "not an IFC file"}`);
      return;
    }

    // Every supported file previews -- meshing runs in a worker
    // (meshWorker.ts), so size no longer threatens the main thread;
    // the degrade path survives only for files web-ifc cannot parse.
    onPreview(file);

    setUploading(true);
    setUploadError(null);
    setProgress(new Map());

    // THE INTAKE DOOR, three steps: mint, put, name the object. Refusals surface at each --
    // the typed client never throws on HTTP refusals, and a swallowed 403 reads as silence (seen live
    // 2026-08-14: "no progress" was a membershipless upload).
    try {
      const response = await typedClient.POST('/v1/intake/urls', {
        body: {
          project_id: projectId,
          document_name: file.name,
          size_bytes: file.size,
        },
      });
      if (response.error) {
        setUploadError(response.error instanceof Error ? response.error.message : String(response.error));
        setUploading(false);
        return;
      }
      if (!response.response?.ok) {
        setUploadError(errorMessage(response));
        setUploading(false);
        return;
      }
      const minted = response.data as IntakeUrl;
      setSent({ bytes: 0, total: file.size });
      await putToUrl(
        minted.put_url,
        minted.content_type,
        file,
        (bytes, total) => setSent({ bytes, total }),
        running,
      );
      setSent(null);
      await announceArrival(minted.object_name, file.name);
    } catch (e) {
      console.error(e);
      setSent(null);
      setUploadError(e instanceof Error ? e.message : String(e));
      setUploading(false);
    }
  };

  return (
    <Stack direction="column" gap="sm">
      <Dropzone
        label={uploading ? "Ingesting..." : "Upload IFC File"}
        accept={[".ifc"]}
        busy={uploading}
        onFile={(file) => void handleFileUpload(file)}
        onRefused={setUploadError}
        hint={sniff ? `${sniff.schema} · ${sniff.totalEntities.toLocaleString()} entities` : undefined}
      />
      {uploadError && (
        <Notice
          kind="error"
          actions={
            <>
              {uploadError.includes("onboard") && (
                <RouteLink to="/onboard" tone="brand" size="control">
                  Onboard →
                </RouteLink>
              )}
              {unannounced && (
                <ActionButton size="sm" onPress={retryArrival} isDisabled={uploading}>
                  Retry {unannounced.name} →
                </ActionButton>
              )}
            </>
          }
        >
          {uploadError}
        </Notice>
      )}
      {sent !== null && (
        <Text tone="secondary">
          Uploading: {Math.floor((sent.bytes / Math.max(sent.total, 1)) * 100)}%
        </Text>
      )}
      {progress.size > 0 && (
        <Stack direction="column" gap="xs">
          {Array.from(progress.entries()).map(([file, p]) => (
            <Text key={file} tone="secondary">
              Parsing{progress.size > 1 ? ` ${file.split("/").pop()}` : ""}: {p.staged} / {p.total}
            </Text>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
